import webApiClient from './client';

export const classApi = {
    // Terms
    getTerms: () => webApiClient.get('/api/v2/classes/terms'),
    createTerm: (data) => webApiClient.post('/api/v2/classes/terms', data),
    updateTerm: (id, data) => webApiClient.put(`/api/v2/classes/terms/${id}`, data),
    deleteTerm: (id) => webApiClient.delete(`/api/v2/classes/terms/${id}`),
    rolloverTerm: (id, targetTermId) => webApiClient.post(`/api/v2/classes/terms/${id}/rollover`, { targetTermId }),
    copyClasses: (termId, sourceTermId, classIds) => webApiClient.post(`/api/v2/classes/terms/${termId}/copy-classes`, { sourceTermId, classIds }),
    openEnrolment: (id) => webApiClient.post(`/api/v2/classes/terms/${id}/open-enrolment`),
    sendEnrolmentReminders: (termId, overrideSubject, overrideHtml) => webApiClient.post(`/api/v2/classes/terms/${termId}/send-enrolment-reminders`, { ...(overrideSubject && { overrideSubject }), ...(overrideHtml && { overrideHtml }) }, { timeout: 30000 }),
    sendRolloverNotifications: (termId, overrideSubject, overrideHtml) => webApiClient.post(`/api/v2/classes/terms/${termId}/send-rollover-notifications`, { ...(overrideSubject && { overrideSubject }), ...(overrideHtml && { overrideHtml }) }, { timeout: 30000 }),

    // Class Definitions
    getClasses: (termId) => webApiClient.get(`/api/v2/classes/definitions${termId ? `?termId=${termId}` : ''}`),
    getClassesForImport: () => webApiClient.get('/api/v2/classes/definitions'),
    getTonightClasses: (date = null) => {
        const qs = date ? `?date=${date}` : '';
        return webApiClient.get(`/api/v2/classes/definitions/tonight${qs}`);
    },
    createClass: (data) => webApiClient.post('/api/v2/classes/definitions', data),
    updateClass: (id, data) => webApiClient.put(`/api/v2/classes/definitions/${id}`, data),
    deleteClass: (id) => webApiClient.delete(`/api/v2/classes/definitions/${id}`),

    // Enrolments
    getEnrolments: (classId, date) => {
        const qs = date ? `&date=${date}` : '';
        return webApiClient.get(`/api/v2/classes/enrolments?classId=${classId}${qs}`);
    },
    createEnrolment: (data) => webApiClient.post('/api/v2/classes/enrolments', data),
    updateEnrolmentStatus: (id, status) => webApiClient.put(`/api/v2/classes/enrolments/${id}/status`, { status }),
    updateEnrolmentRole: (id, role) => webApiClient.put(`/api/v2/classes/enrolments/${id}/status`, { role }),
    deleteEnrolment: (id) => webApiClient.delete(`/api/v2/classes/enrolments/${id}`),

    // Check-In
    checkIn: (classId, studentEnrolmentId, isMakeup = false, isWalkIn = false, date) =>
        webApiClient.post('/api/v2/classes/checkin', { classId, studentEnrolmentId, isMakeup, isWalkIn, ...(date && { date }) }),
    undoCheckIn: (classId, studentEnrolmentId, date) =>
        webApiClient.post('/api/v2/classes/checkin/undo', { classId, studentEnrolmentId, ...(date && { date }) }),
    checkInShortCourse: (shortCourseId, studentEnrolmentId, date) =>
        webApiClient.post('/api/v2/classes/checkin', { shortCourseId, studentEnrolmentId, ...(date && { date }) }),
    undoCheckInShortCourse: (shortCourseId, studentEnrolmentId, date) =>
        webApiClient.post('/api/v2/classes/checkin/undo', { shortCourseId, studentEnrolmentId, ...(date && { date }) }),
    getShortCourseEnrolments: (shortCourseId, date) => {
        const qs = date ? `&date=${date}` : '';
        return webApiClient.get(`/api/v2/classes/enrolments?shortCourseId=${shortCourseId}${qs}`);
    },
    getCheckInStats: (classId) => webApiClient.get(`/api/v2/classes/checkin/stats?classId=${classId}`),

    // Sessions
    getSessions: (classId, from, to) => webApiClient.get(`/api/v2/classes/sessions?classId=${classId}${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}`),
    createSession: (classId, sessionDate) => webApiClient.post('/api/v2/classes/sessions', { classId, sessionDate }),
    updateSessionInstructors: (id, actualInstructors) => webApiClient.put(`/api/v2/classes/sessions/${id}/instructors`, { actualInstructors }),
    lockSession: (sessionId, isLocked) => webApiClient.patch(`/api/v2/classes/sessions/${sessionId}/lock`, { isLocked }),
    getInstructorReport: (from, to) => webApiClient.get(`/api/v2/classes/instructor-report?from=${from}&to=${to}`),
    getSubstitutionReport: (from, to) => webApiClient.get(`/api/v2/classes/instructor-substitutions?from=${from}&to=${to}`),
    getAttendance: (studentId) => webApiClient.get(`/api/v2/classes/attendance/${studentId}`),
    getAttendanceReport: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.termId) qs.set('termId', params.termId);
        if (params.classId) qs.set('classId', params.classId);
        if (params.from) qs.set('from', params.from);
        if (params.to) qs.set('to', params.to);
        const queryString = qs.toString();
        return webApiClient.get(`/api/v2/classes/attendance/report${queryString ? `?${queryString}` : ''}`);
    },

    getEnrolmentHistory: (params = {}) => {
        const q = new URLSearchParams();
        if (params.termId) q.set('termId', params.termId);
        if (params.classId) q.set('classId', params.classId);
        if (params.status) q.set('status', params.status);
        const qs = q.toString();
        return webApiClient.get(`/api/v2/classes/students/enrolment-history${qs ? `?${qs}` : ''}`);
    },

    // Students
    searchStudents: (q) => webApiClient.get(`/api/v2/classes/students/search?q=${encodeURIComponent(q)}`),
    createWalkIn: (data) => webApiClient.post('/api/v2/classes/students/walk-in', data),
    sendInvite: (id) => webApiClient.post(`/api/v2/classes/students/${id}/invite`),
    getAllStudents: (filter, termId) => {
        const params = new URLSearchParams();
        if (filter) params.set('filter', filter);
        if (termId) params.set('termId', termId);
        const qs = params.toString();
        return webApiClient.get(`/api/v2/classes/students/all${qs ? `?${qs}` : ''}`);
    },
    getStudentHistory: (studentId) => webApiClient.get(`/api/v2/classes/students/${studentId}/history`),
    adminEnrolStudent: (studentId, classIds) => webApiClient.post(`/api/v2/classes/students/${studentId}/enrol`, { classIds }),
    setStudentInactive: (studentId, isInactive) => webApiClient.patch(`/api/v2/classes/students/${studentId}`, { isInactive }),
    bulkCheckDuplicates: (students) => webApiClient.post('/api/v2/classes/students/bulk-check-duplicates', { students }),
    bulkImportStudents: (students, enrolments = []) => webApiClient.post('/api/v2/classes/students/bulk-import', { students, enrolments }),
    bulkSendInvites: (studentProfileIds) => webApiClient.post('/api/v2/classes/students/bulk-send-invites', { studentProfileIds }, { timeout: 60000 }),
    updateStudentProfile: (studentId, data) => webApiClient.patch(`/api/v2/classes/students/${studentId}/profile`, data),
    uploadStudentPhoto: (studentId, base64Image) =>
        webApiClient.patch(`/api/v2/classes/students/${studentId}/photo`, { base64Image }),
    getStudentPhotoBase64: (studentId) =>
        webApiClient.get(`/api/v2/classes/students/${studentId}/photo`),
    deleteStudentPhoto: (studentId) =>
        webApiClient.delete(`/api/v2/classes/students/${studentId}/photo`),
    // Org-scoped roster removal: unlink (account holder) / de-identify (school-only w/ payments) / remove (no payments)
    removeStudentFromRoster: (studentId) =>
        webApiClient.delete(`/api/v2/classes/students/${studentId}/roster`),
    // School URL management
    getSchoolUrl: () => webApiClient.get('/api/v2/classes/school-url'),
    updateSchoolUrl: (orgSlug) => webApiClient.put('/api/v2/classes/school-url', { orgSlug }),

    // URL Groups
    getGroups: () => webApiClient.get('/api/v2/classes/groups'),
    createGroup: (data) => webApiClient.post('/api/v2/classes/groups', data),
    updateGroup: (id, data) => webApiClient.put(`/api/v2/classes/groups/${id}`, data),
    deleteGroup: (id) => webApiClient.delete(`/api/v2/classes/groups/${id}`),
    reorderGroup: (id, classIds) => webApiClient.put(`/api/v2/classes/groups/${id}/reorder`, { classIds }),

    // Public school pages (unauthenticated)
    getPublicSchool: (orgSlug, termId) => webApiClient.get(`/api/v2/classes/school/${orgSlug}${termId ? `?term=${encodeURIComponent(termId)}` : ''}`),
    getPublicGroupPage: (orgSlug, groupSlug) => webApiClient.get(`/api/v2/classes/school/${orgSlug}/groups/${groupSlug}`),
    getPublicCoursePage: (orgSlug, courseSlug) => webApiClient.get(`/api/v2/classes/school/${orgSlug}/course/${courseSlug}`),
    getBrowseableStyles: (orgSlug) => webApiClient.get(`/api/v2/classes/school/${orgSlug}/styles`),
    lookupStudent: (orgSlug, email) => webApiClient.get(`/api/v2/classes/school/${orgSlug}/lookup-student?email=${encodeURIComponent(email)}`),
    submitEnquiry: (orgSlug, data) => webApiClient.post(`/api/v2/classes/school/${orgSlug}/enquire`, data),

    // Short Courses
    getShortCourses: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.shortCourseGroupId) params.append('shortCourseGroupId', filters.shortCourseGroupId);
        const qs = params.toString();
        return webApiClient.get(`/api/v2/classes/short-courses${qs ? `?${qs}` : ''}`);
    },
    getShortCourse: (id) => webApiClient.get(`/api/v2/classes/short-courses/${id}`),
    createShortCourse: (data) => webApiClient.post('/api/v2/classes/short-courses', data),
    updateShortCourse: (id, data) => webApiClient.put(`/api/v2/classes/short-courses/${id}`, data),
    deleteShortCourse: (id) => webApiClient.delete(`/api/v2/classes/short-courses/${id}`),
    getShortCourseEnrolments: (shortCourseId) => webApiClient.get(`/api/v2/classes/enrolments?shortCourseId=${shortCourseId}`),
    updateShortCourseRestriction: (id, restrictLeaders, restrictFollowers) =>
        webApiClient.patch(`/api/v2/classes/short-courses/${id}/restriction`, { restrictLeaders, restrictFollowers }),
    updateShortCourseInstructors: (id, instructorIds) =>
        webApiClient.patch(`/api/v2/classes/short-courses/${id}/instructors`, { primaryInstructors: instructorIds }),
    createShortCourseSession: (classId, sessionDate) =>
        webApiClient.post(`/api/v2/classes/short-courses/${classId}/session`, { sessionDate }),

    // Short Course Groups
    getShortCourseGroups: () => webApiClient.get('/api/v2/classes/short-course-groups'),
    getShortCourseGroup: (id) => webApiClient.get(`/api/v2/classes/short-course-groups/${id}`),
    createShortCourseGroup: (data) => webApiClient.post('/api/v2/classes/short-course-groups', data),
    updateShortCourseGroup: (id, data) => webApiClient.put(`/api/v2/classes/short-course-groups/${id}`, data),
    deleteShortCourseGroup: (id) => webApiClient.delete(`/api/v2/classes/short-course-groups/${id}`),

    // Admin management
    getEnquiries: (status) => webApiClient.get(`/api/v2/classes/admin/enquiries${status ? `?status=${status}` : ''}`),
    placeEnquiry: (id, classId) => webApiClient.put(`/api/v2/classes/admin/enquiries/${id}/place`, { classId }),
    dismissEnquiry: (id) => webApiClient.put(`/api/v2/classes/admin/enquiries/${id}/dismiss`, {}),
    getUpgradeRequests: () => webApiClient.get('/api/v2/classes/admin/upgrade-requests'),
    resolveUpgradeRequest: (id, decision, note) => webApiClient.put(`/api/v2/classes/admin/upgrade-requests/${id}`, { decision, note }),
    getMissingReEnrolments: () => webApiClient.get('/api/v2/classes/admin/missing-re-enrolments'),

    // Phase 2: Pricing Options (B11)
    getPricingOptions: () => webApiClient.get('/api/v2/classes/pricing'),
    createPricingOption: (data) => webApiClient.post('/api/v2/classes/pricing', data),
    updatePricingOption: (id, data) => webApiClient.put(`/api/v2/classes/pricing/${id}`, data),
    deletePricingOption: (id) => webApiClient.delete(`/api/v2/classes/pricing/${id}`),
    reorderPricingOptions: (items) => webApiClient.put('/api/v2/classes/pricing/reorder', { items }),

    // Phase 2: Payment Calculation & QR (B12)
    calculatePayment: (studentProfileId, orgId, customAmount, viewedTermId) => webApiClient.post('/api/v2/classes/payments/calculate', { studentProfileId, orgId, ...(customAmount !== undefined && { customAmount }), ...(viewedTermId && { viewedTermId }) }),
    generateQrToken: (studentProfileId, orgId, customAmount, pricingOptionId, enrolmentIds) => webApiClient.post('/api/v2/classes/payments/generate-qr', { studentProfileId, orgId, ...(customAmount !== undefined && { customAmount }), ...(pricingOptionId && { pricingOptionId }), ...(enrolmentIds?.length && { enrolmentIds }) }),
    sendPaymentEmail: (studentProfileId, orgId, customAmount, enrolmentIds, overrideSubject, overrideHtml, pricingOptionId) => webApiClient.post('/api/v2/classes/payments/send-email', { studentProfileId, orgId, ...(customAmount !== undefined && { customAmount }), ...(enrolmentIds?.length && { enrolmentIds }), ...(overrideSubject && { overrideSubject }), ...(overrideHtml && { overrideHtml }), ...(pricingOptionId && { pricingOptionId }) }),
    studentGenerateQrToken: (enrolmentId) => webApiClient.post('/api/v2/classes/payments/student-generate-qr', { enrolmentId }),
    // Collect an EXISTING payment/adjustment by id (drop-in fee, manual adjustment, etc.) —
    // QR for instant Stripe, or email a payment-link reminder. Org resolved server-side.
    collectPaymentQr: (paymentId) => webApiClient.post('/api/v2/classes/payments/collect-qr', { paymentId }),
    collectPaymentEmail: (paymentId, overrideSubject, overrideHtml) => webApiClient.post('/api/v2/classes/payments/collect-email', { paymentId, ...(overrideSubject && { overrideSubject }), ...(overrideHtml && { overrideHtml }) }),
    confirmEnrolment: (enrolmentId) => webApiClient.post('/api/v2/classes/student/confirm-enrolment', { enrolmentId }),
    getEnrolmentByToken: (token) => webApiClient.get(`/api/v2/classes/enrolments/confirm-by-token?token=${encodeURIComponent(token)}`),
    confirmEnrolmentByToken: (token, enrolmentIds) => webApiClient.post('/api/v2/classes/enrolments/confirm-by-token', { token, ...(Array.isArray(enrolmentIds) ? { enrolmentIds } : {}) }),
    getStudentEnrolments: async () => {
        const response = await webApiClient.get('/api/v2/classes/student/my-enrolments');
        if (!response.ok || !response.data || !response.data.enrolments) {
            throw new Error(response.data?.message || 'Failed to fetch student enrolments');
        }
        return response.data.enrolments;
    },
    resolveQrToken: (token) => webApiClient.get(`/api/v2/classes/payments/qr/${token}`),
    getMyPendingPayment: () => webApiClient.get('/api/v2/classes/payments/student/my-pending-payment'),

    // Phase 2: Stripe payment (B13)
    createPaymentIntent: (paymentId) => webApiClient.post('/api/v2/classes/payments/create-intent', { paymentId }),
    confirmPayment: (paymentId, stripePaymentIntentId) => webApiClient.post('/api/v2/classes/payments/confirm', { paymentId, stripePaymentIntentId }),

    // Phase 2: Student invite claim (B19)
    previewInvite: (token) => webApiClient.get(`/api/v2/classes/students/claim/preview?token=${encodeURIComponent(token)}`),
    claimInvite: (data) => webApiClient.post('/api/v2/classes/students/claim', data),
    getPublicOrgLogo: (imageKey) => webApiClient.get(`/api/v2/classes/public/org-logo?imageKey=${encodeURIComponent(imageKey)}`),

    // Phase 2: Class cancellation (B20)
    cancelClassPreview: (classId) => webApiClient.get(`/api/v2/classes/definitions/${classId}/cancel-preview`),
    cancelClass: (classId, reason, refunds) => webApiClient.post(`/api/v2/classes/definitions/${classId}/cancel`, { reason, refunds }),

    // Session cancellation (refunds issued at cancel-time, gated by offersRefunds)
    getClassesForDate: (date) => webApiClient.get(`/api/v2/classes/sessions/classes-for-date?date=${date}`),
    cancelSessions: (date, classIds, reason, refunds) => webApiClient.post('/api/v2/classes/sessions/cancel-tonight', { date, classIds, reason, refunds }),

    // Trial Deals
    getTrialDeals: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.status) params.set('status', filters.status);
        if (filters.eligibleClassId) params.set('eligibleClassId', filters.eligibleClassId);
        const qs = params.toString();
        return webApiClient.get(`/api/v2/classes/trial-deals${qs ? `?${qs}` : ''}`);
    },
    getTrialDeal: (id) => webApiClient.get(`/api/v2/classes/trial-deals/${id}`),
    createTrialDeal: (data) => webApiClient.post('/api/v2/classes/trial-deals', data),
    updateTrialDeal: (id, data) => webApiClient.put(`/api/v2/classes/trial-deals/${id}`, data),
    deleteTrialDeal: (id) => webApiClient.delete(`/api/v2/classes/trial-deals/${id}`),
    checkTrialSlug: (slug, excludeId) => {
        const params = new URLSearchParams({ slug });
        if (excludeId) params.set('excludeId', excludeId);
        return webApiClient.get(`/api/v2/classes/trial-deals/check-slug?${params.toString()}`);
    },
    getPublicTrialDeal: (slug) => webApiClient.get(`/api/v2/classes/public/trial-deals/${slug}`),
    checkTrialDealEmail: (slug, email) => webApiClient.get(`/api/v2/classes/public/trial-deals/${slug}/check-email?email=${encodeURIComponent(email)}`),
    createTrialPurchaseIntent: (slug, data) => webApiClient.post(`/api/v2/classes/public/trial-deals/${slug}/create-intent`, data),

    // Prospects
    getProspects: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.status) params.set('status', filters.status);
        if (filters.prospectSource) params.set('prospectSource', filters.prospectSource);
        if (filters.leadSource) params.set('leadSource', filters.leadSource);
        const qs = params.toString();
        return webApiClient.get(`/api/v2/classes/prospects${qs ? `?${qs}` : ''}`);
    },
    getProspectDetail: (id) => webApiClient.get(`/api/v2/classes/prospects/${id}`),
    markProspectConverted: (id) => webApiClient.patch(`/api/v2/classes/prospects/${id}/status`, { status: 'converted' }),
    markProspectLost: (id) => webApiClient.patch(`/api/v2/classes/prospects/${id}/status`, { status: 'lost' }),
    updateProspectLeadSource: (id, leadSource, leadSourceDetail) => webApiClient.patch(`/api/v2/classes/prospects/${id}/lead-source`, { leadSource, leadSourceDetail }),

    // Enrolment removal
    removeEnrolment: (enrolmentId, refundAmount, issueRefund) => webApiClient.post(`/api/v2/classes/enrolments/${enrolmentId}/remove`, { refundAmount, issueRefund }),
    // Issue a refund against an enrolment. Partial keeps the student enrolled; a full refund cancels the enrolment.
    refundEnrolment: (enrolmentId, refundAmount) => webApiClient.post(`/api/v2/classes/enrolments/${enrolmentId}/refund`, { refundAmount }),
    markExternalPayment: (studentProfileId, orgId, method, notes, enrolmentIds, instalmentContext, assertedAmountCents) => webApiClient.post('/api/v2/classes/payments/mark-external', { studentProfileId, orgId, method, notes, ...(enrolmentIds?.length && { enrolmentIds }), ...(instalmentContext && { instalmentContext }), ...(Number.isFinite(assertedAmountCents) && { assertedAmountCents }) }),
    resolvePriorPayment: (paymentId, orgId, resolution, reason, method) => webApiClient.post('/api/v2/classes/payments/resolve-prior', { paymentId, orgId, resolution, ...(reason && { reason }), ...(method && { method }) }),

    // Per-student cross-term payment audit (Payments tab — student-payment-audit-tab).
    // List + single-student card are org-scoped server-side (no orgId param needed).
    getPaymentAuditList: () => webApiClient.get('/api/v2/classes/payments/audit'),
    getPaymentAudit: (studentProfileId) => webApiClient.get(`/api/v2/classes/payments/audit/${studentProfileId}`),
    // amount is in DOLLARS; the backend converts to integer cents at the boundary.
    // enrolmentId (optional) binds the charge to a single enrolment, e.g. a walk-in drop-in fee.
    createManualCharge: (studentProfileId, amount, reason, enrolmentId) => webApiClient.post('/api/v2/classes/payments/manual-charge', { studentProfileId, amount, reason, ...(enrolmentId && { enrolmentId }) }),
};
